import ThermalPrinterEncoder, {
  ThermalPrinterEncoderOption,
} from "../thermal-printer-encoder"
import { Business, DEFAULT_MPOS_SETTING, BIR_LABEL } from "../models"
import { format, parseISO } from "date-fns"
import {
  getActualCashChange,
  getActualCashReceived,
  GetCartTransactionResponse,
} from "../../hooks/api/use-cart-transaction"
import {
  InvoiceSettings,
  InvoiceSettingsResponseBody,
} from "../../hooks/api/use-invoice-settings"
import { DELIVERECT_ORDER_TYPE_LABELS } from "../deliverect"
import IminPrinter from "../imin-printer"
import { formatPaymentMethod, printBorder, printSplitTable } from "./helper"
import axios from "axios"
import { nanoid } from "nanoid"
import Big from "big.js"
import { formatNumberToCurrency } from "../currency"
import { BASE_URL } from "../../constants/Config"

export interface RefundReceiptDetails {
  orderNumber?: string
  refundAmount: number
  paymentMethod?: string
  refundedBy?: string
  refundedAt?: string
  currency: string
}

export const encodeCartTransactionReceiptMetadata = async (
  type: "Header" | "Footer",
  business: Business,
  trx: GetCartTransactionResponse,
  receiptSetting: InvoiceSettingsResponseBody[number],
  posSetting: typeof DEFAULT_MPOS_SETTING,
  width: number,
  encoder: ThermalPrinterEncoder,
  hasTaxes?: boolean
) => {
  const dueDate = (e: ThermalPrinterEncoder) => {
    if (!trx.dueDate) return e

    return e
      .align("left")
      .line(
        `Due Date: ${format(parseISO(trx.dueDate as string), "dd/MM/yyyy")}`
      )
  }

  const vat = (e: ThermalPrinterEncoder) => {
    let updatedVAT = e
      .align("center")
      .line(trx.location.companyName || "")
      .line(`${hasTaxes ? "VAT REG " : ""}TIN: ${trx.location.tin}`)
      .line(`${trx.location.addressLine1}`)
      .line(`SN No.: ${trx.cashRegister?.serialNo}`)
      .line(`MIN: ${trx.cashRegister?.min}`)
      .newline()

    if (business.id !== "9e800468-ce7e-4f26-ae19-61656ee51953") {
      updatedVAT = updatedVAT.line("THIS SERVES AS YOUR OFFICIAL RECEIPT")
    }

    return updatedVAT
  }

  const transactionType = (e: ThermalPrinterEncoder) => {
    return e
      .align("center")
      .line(
        `Transaction Type: ${formatPaymentMethod(
          trx.paymentMethodCode,
          trx.paymentChannel
        )}`
      )
  }

  const orderType = (e: ThermalPrinterEncoder) => {
    return e.align("center").line(`Order Type: ${trx.orderType}`)
  }

  const phone = (e: ThermalPrinterEncoder) => {
    return e.align("center").line(business.phone)
  }

  const customer = (e: ThermalPrinterEncoder) => {
    let result = printBorder(e, width, { noSpacing: true })
    if (trx.isWalkIn) {
      result = result.align("center").line("Customer: Walk In")
      result = printBorder(result, width, { noSpacing: true }).line("\n")
      return result
    } else {
      let address = trx.customerLocation?.addressLine1
      if (trx.customerLocation?.addressLine2) {
        address += `, ${trx.customerLocation?.addressLine2}`
      }
      if (trx.customerLocation?.city) {
        address += `, ${trx.customerLocation?.city}`
      }
      if (trx.customerLocation?.province) {
        address += `, ${trx.customerLocation?.province}`
      }
      if (trx.customerLocation?.zip) {
        address += `, ${trx.customerLocation?.zip}`
      }

      result = e
        .align("left")
        .line(`Customer: ${trx.customer?.fullName || "-"}`)
        .line(`Phone: ${trx.customer?.phone || "-"}`)
        .line(`Address: ${address || "-"}`)

      if (trx.isPickup && trx.pickupTime) {
        let pickupTimeFormatted = ""
        try {
          pickupTimeFormatted = format(parseISO(trx.pickupTime), "dd/MM/yyyy")
        } catch (_) {}
        if (pickupTimeFormatted) {
          result = e.line(`Picked up: ${pickupTimeFormatted}`)
        }
      }

      // if (trx.tables?.[0]?.name) {
      //   result = result.line(`Table: ${trx.tables[0]?.name}`)
      // }
      if (trx?.rewardedPoints != null) {
        result = result
          .line(`Rewarded Points: ${trx.rewardedPoints}`)
          // .newline()
          .newline()
      }
      if (trx?.rewardedCashback != null) {
        result = result
          .line(`Rewarded Cashback: ${trx.rewardedCashback}`)
          // .newline()
          .newline()
      }
      if (trx?.customer?.cashback != null) {
        result = result
          .line(`Total Cashback: ${trx.customer?.cashback}`)
          // .newline()
          .newline()
      }
      result = printBorder(e, width, { noSpacing: true }).line("\n")
      return result
    }
  }

  const note = (e: ThermalPrinterEncoder) => {
    return e
      .align("center")
      .line("Note: " + trx.notes)
      .newline()
  }

  const barcode = (e: ThermalPrinterEncoder) => {
    return e
      .align("center")
      .barcode(trx.orderNumber.toString(), "code39", {
        width: 1,
        text: true,
      })
      .newline()
  }

  const storeName = (e: ThermalPrinterEncoder) => {
    return e.align("center").line(trx.location.name)
  }

  const location = (e: ThermalPrinterEncoder) => {
    return e.align("center").line(trx.location.addressLine1).newline()
  }

  const message = (e: ThermalPrinterEncoder) => {
    return e.align("center").line(receiptSetting.message).newline()
  }

  const disclaimer = (e: ThermalPrinterEncoder) => {
    return e
      .align("center")
      .line(`Disclaimer: ${receiptSetting.disclaimer || ""}`)
  }

  const orderNumber = (e: ThermalPrinterEncoder) => {
    if (trx.queueNumber) {
      return e.align("center").line("Order #:").line(`${trx.queueNumber}`)
    }
    return e
  }

  const referenceNumber = (e: ThermalPrinterEncoder) => {
    return e.align("center").line("Reference #:").line(`${trx.paymentId}`)
  }

  const qrCode = (e: ThermalPrinterEncoder) => {
    return e
      .newline()
      .align("center")
      .line("Scan to access your portal")
      .newline()
      .qrcode(
        trx.customer?.id
          ? `${BASE_URL}/customer-portal/${trx.customer?.id}?currency=${business?.businessCurrency}`
          : `${BASE_URL}/share/receipt/${trx.id}`
      )
  }

  let result = encoder.initializeWithMultiByte()

  const logo = receiptSetting?.printLogo || business?.logoUrl || ""
  if (receiptSetting.showLogo && logo && type === "Header") {
    try {
      const loadImage = (src: string) => {
        return new Promise<HTMLImageElement>((resolve, reject) => {
          axios
            .get(src + `?v=${nanoid()}`, {
              responseType: "blob",
              headers: {
                "Cache-Control": "no-cache",
              },
            })
            .then((response) => {
              const img = new Image()
              img.crossOrigin = "anonymous"
              img.src = URL.createObjectURL(response.data)

              img.onload = () => {
                URL.revokeObjectURL(img.src) // Clean up
                resolve(img)
              }
              img.onerror = (err) => {
                URL.revokeObjectURL(img.src)
                reject(err)
              }
            })
        })
      }
      const image = await loadImage(logo)
      result = encoder
        .align("center")
        .image(image, 160, 160, "atkinson")
        .newline()
    } catch (e) {
      console.log(e)
    }
  }

  if (type === "Header" && receiptSetting.showCustomerQrCode === "Header") {
    result = qrCode(result)
  }

  const businessName = business.name.trim()
  if (type === "Header" && businessName) {
    result = result
      .newline()
      .align("center")
      .bold(true)
      .line(businessName)
      .bold(false)
      .newline()
  }

  if (receiptSetting.storeName === type) {
    result = storeName(result)
  }
  if (receiptSetting.showLocation === type) {
    result = location(result)
  }
  if (receiptSetting.showTaxPIN && receiptSetting.taxPIN && type === "Header") {
    result = result
      .align("center")
      .line(`Tax PIN: ${receiptSetting.taxPIN}`)
      .newline()
  }
  if (
    receiptSetting.showDueDate === type &&
    trx.paymentMethodCode === "PAY LATER"
  ) {
    result = dueDate(result)
  }
  if (trx.location.isVatRegistered && trx.location.isVatRegistered === "Yes") {
    result = vat(result)
  }

  if (receiptSetting.transactionType === type) {
    result = transactionType(result)
  }
  if (receiptSetting.showOrderType === type) {
    result = orderType(result)
  }
  if (
    receiptSetting.showReferenceNumber &&
    type === "Header" &&
    trx.paymentId
  ) {
    result = referenceNumber(result)
  }
  if (type === "Header") {
    result = result
      .align("center")
      .line(new Date(trx.createdAt).toLocaleString())
  }
  if (receiptSetting.showPhone === type) {
    result = phone(result)
  }
  if (type === "Header") {
    result = result
      .align("center")
      .line("Cashier: " + trx.createdBy)
      .newline()
  }
  if (
    type === "Header" &&
    receiptSetting.showDisclaimer === "Header" &&
    receiptSetting.disclaimer
  ) {
    result = disclaimer(result)
  }
  if (receiptSetting.showCustomerInfo === type) {
    result = customer(result)
  }
  if (receiptSetting.showOrderNote === type && trx.notes) {
    result = note(result)
  }
  if (receiptSetting.showBarcode === type) {
    result = barcode(result)
  }
  // else {
  //   result = result.align("center").text(`Order #${trx.orderNumber}`)
  // }

  if (receiptSetting.showMessage === type && receiptSetting.message) {
    result = message(result)
  }
  if (
    type === "Footer" &&
    receiptSetting.showDisclaimer === "Footer" &&
    receiptSetting.disclaimer
  ) {
    result = disclaimer(result)
  }

  if (type === "Footer" && receiptSetting.showCustomerQrCode === "Footer") {
    result = qrCode(result).newline().newline().newline().newline()
  }

  if (type === "Header") {
    result = orderNumber(result).newline()
  }

  const shipment = trx.shipment
  if (shipment?.shipmentType === "POS_MANUAL" && type === "Header") {
    result = printBorder(result, width, { noSpacing: true })

    const { quoteDetails } = shipment
    const {
      bookingCode,
      customerName,
      address,
      city,
      phoneNumber,
      courierName,
    } = quoteDetails
    result = printSplitTable(result, width, width / 2, [
      [`Order Number: ${trx.orderNumber}`, `Booking Code: ${bookingCode}`],
    ])
      .align("left")
      .line(`Customer: ${customerName || "-"}`)
      .line(`Address: ${address || "-"}`)
      .line(`City: ${city || "-"}`)
      .line(`Phone Number: ${phoneNumber || "-"}`)

    if (courierName) {
      result = result.line(`Courier Name: ${courierName}`)
    }

    result = printBorder(result, width, { noSpacing: true })
  }
  result = result.align("left")
  return result
}

export const encodeCartTransactionReceiptItemHeader = (
  trx: GetCartTransactionResponse,
  width: number,
  encoder: ThermalPrinterEncoder
) => {
  return printSplitTable(encoder, width, 10, [
    ["Item(s)", trx.orderedProducts.length.toString()],
  ])
}

export const encodeCartTransactionReceiptTotal = (
  trx: GetCartTransactionResponse,
  width: number,
  currency: string,
  receiptSetting: InvoiceSettings,
  encoder: ThermalPrinterEncoder,
  business?: Business
) => {
  const totalIncludedTax =
    trx.taxes
      ?.filter((el) => el.type === "INCLUDED")
      .reduce((acc, el) => acc + el.amount, 0) || 0

  // Special logic for business ID "865ca7d1-e12a-401e-8c71-92f7a3ec2aa4"
  const shouldDeductIncludedTax =
    business?.id === "865ca7d1-e12a-401e-8c71-92f7a3ec2aa4"
  const adjustedTotalCartAmount = shouldDeductIncludedTax
    ? trx.totalCartAmount - totalIncludedTax
    : trx.totalCartAmount

  let rows: Array<[string, string]> = [
    [
      "Subtotal",
      formatNumberToCurrency(currency, adjustedTotalCartAmount, "code"),
    ],
  ]

  if (trx.tax > 0) {
    rows.push(["Tax", formatNumberToCurrency(currency, trx.tax, "code")])
    trx.taxes?.forEach((t) => {
      rows.push([
        `- ${t.name} ${t.rate}% ${t.type === "INCLUDED" ? "(Included)" : ""}`,
        formatNumberToCurrency(currency, t.amount, "code"),
      ])
    })
  }

  if (trx.serviceCharge) {
    rows.push([
      "Service Charge",
      formatNumberToCurrency(currency, trx.serviceCharge, "code"),
    ])
  }

  if (trx.shippingCost) {
    rows.push([
      "Shipping Cost",
      formatNumberToCurrency(currency, trx.shippingCost, "code"),
    ])
  }

  if (trx.cashbackApplied) {
    rows.push([
      "Cashback Applied",
      formatNumberToCurrency(currency, trx.cashbackApplied, "code"),
    ])
  } else {
    const discount = trx.nonTaxDiscounts || trx.discount
    if (discount && discount > 0) {
      // Check if we have promotion details
      if (trx.promotionDetails && trx.promotionDetails.length > 0) {
        // Display each promotion on its own line
        trx.promotionDetails.forEach((promotion) => {
          let discountName = promotion.name || "Discount"

          // Use BIR label for special codes
          if (promotion.code) {
            discountName = BIR_LABEL[promotion.code] || promotion.code
          }

          // Add percentage if available
          if (promotion.discountPercentage) {
            discountName = `${discountName} ${promotion.discountPercentage}%`
          }

          // Calculate the discount amount for this promotion
          // If there's only one promotion, use the total discount
          // Otherwise, we'd need individual discount amounts per promotion
          const discountAmount =
            trx.promotionDetails?.length === 1 ? discount : discount // For multiple promotions, this would need per-promotion amounts

          rows.push([
            discountName,
            formatNumberToCurrency(currency, discountAmount, "code"),
          ])
        })
      } else {
        // Fallback to generic "Discount" if no promotion details
        rows.push([
          "Discount",
          formatNumberToCurrency(currency, discount, "code"),
        ])
      }
    }
  }

  rows.push(["Total", formatNumberToCurrency(currency, trx.grandTotal, "code")])

  if (
    ["PAY LATER", "INVOICE"].includes(trx.paymentMethodCode) ||
    (trx.orderStatus !== "PAID" && trx.outstanding && trx.outstanding > 0)
  ) {
    rows.push([
      "Total Deposit",
      formatNumberToCurrency(
        currency,
        trx.grandTotal - (trx?.outstanding || 0),
        "code"
      ),
    ])
    rows.push([
      "Total Outstanding",
      formatNumberToCurrency(currency, trx.outstanding || 0, "code"),
    ])
  }

  if (trx.paymentMethodCode === "CASH") {
    rows.push([
      "Amount Received",
      formatNumberToCurrency(currency, trx.cashReceived, "code"),
    ])
    rows.push([
      "Change",
      formatNumberToCurrency(currency, trx.cashChange, "code"),
    ])
  }

  if (trx.paymentMethodCode === "SPLIT_PAYMENT") {
    rows.push([
      "Amount Received",
      formatNumberToCurrency(currency, getActualCashReceived(trx), "code"),
    ])
    rows.push([
      "Change",
      formatNumberToCurrency(currency, getActualCashChange(trx), "code"),
    ])
    const splitPaymentDetails = trx.splitPaymentDetails
    if (splitPaymentDetails && splitPaymentDetails.length) {
      rows.push(["", ""])
      rows.push(["Split Payments", ""])
      splitPaymentDetails.forEach((el) => {
        if (
          receiptSetting.showReferenceNumber &&
          el.paymentId &&
          el.paymentId !== ""
        ) {
          rows.push(["Reference No.:", ""])
          rows.push([el.paymentId, ""])
        }
        const paymentMethod = formatPaymentMethod(
          el.paymentMethodCode,
          el.paymentChannel
        )
        rows.push([paymentMethod, `${el.amount}`])
      })
    }
  }

  if (trx.pickupTime) {
    rows.push(["Pickup Date", format(parseISO(trx.pickupTime), "dd MMM yyyy")])
  }

  if (trx.deliverectOrderType) {
    rows.push([
      "Order Type",
      DELIVERECT_ORDER_TYPE_LABELS[trx.deliverectOrderType],
    ])
    rows.push([
      "Delivery Channel",
      `${trx.source} (${trx.deliverectChannelOrderDisplayId})`,
    ])
    if (trx.deliverectPickupTime) {
      rows.push([
        "Pickup Time",
        format(parseISO(trx.deliverectPickupTime), "dd MMM yyyy HH:mm"),
      ])
    }
    if (trx.deliverectDeliveryTime) {
      rows.push([
        "Delivery Time",
        format(parseISO(trx.deliverectDeliveryTime), "dd MMM yyyy HH:mm"),
      ])
    }
  }

  let result = encoder.newline()
  return printSplitTable(result, width, width / 2, rows)
    .align("left")
    .newline()
}

export const encodeCartTransactionReceiptItem = (
  product: GetCartTransactionResponse["orderedProducts"][number],
  width: number,
  currency: string,
  encoder: ThermalPrinterEncoder,
  isRefunded?: boolean,
  isExchanged?: boolean
) => {
  let totalPrice = Big(product.sellingPrice).mul(product.quantity)
  // if (product.taxIncluded) {
  //   totalPrice = totalPrice.add(product.tax || 0)
  // }
  const unitPrice = totalPrice.div(product.quantity).toNumber()

  let name = `${product.customProductName ?? product.name} - ${product.quantity}x @${formatNumberToCurrency(currency, unitPrice, "code")}`
  encoder = printSplitTable(encoder, width, width - 12, [
    [name, formatNumberToCurrency(currency, totalPrice.toNumber(), "code")],
  ])

  const variantName = product.productOptionValues
    ?.map((el) => el.name)
    .join(" / ")
  if (variantName) {
    encoder = encoder.line(`(${variantName})`)
  }

  if (product.serialNumber && product.serialNumber.length > 0) {
    encoder = encoder.line(`Serial Number:`)
    product.serialNumber.forEach((el) => {
      encoder = encoder.line(el)
    })
  }

  if (product.notes) {
    encoder = encoder.line(`Notes: ${product.notes}`)
  }

  if (!!product.orderedProductAddOns?.length) {
    encoder = encoder.line("Add Ons:")
    product.orderedProductAddOns.forEach((el) => {
      const totalPrice = Big(el.quantity).mul(el.sellingPrice).toFixed(2)
      encoder = encoder.table(
        [
          { width: 20 - 1, marginRight: 2, align: "left" },
          { width: width - 20 - 1, align: "right" },
        ],
        [
          [
            `${el.name} - ${el.quantity}x@${formatNumberToCurrency(currency, el.sellingPrice, "code")}`,
            `${currency} ${totalPrice}`,
          ],
        ]
      )
    })
  }
  if (!!product.employees?.length) {
    encoder = encoder.line("Employees:")
    product.employees.forEach((el) => {
      encoder = encoder.line(el.fullName)
    })
  }

  if (isExchanged) {
    encoder = encoder.line("Exchanged Item")
  } else if (isRefunded) {
    encoder = encoder.line("Returned Item")
  } else if (product.quantityReturned && product.quantityReturned > 0) {
    encoder = encoder.line("Voided")
  }

  return encoder
}

export const encodeOpenDrawer = () => {
  return new ThermalPrinterEncoder({ language: "esc-pos" })
    .raw([0x10, 0x14, 0x00, 0x00, 0x00])
    .pulse()
    .encode()
}

const formatRefundDateValue = (value?: string) => {
  if (!value) {
    return "-"
  }

  try {
    return format(parseISO(value), "PPpp")
  } catch (error) {
    try {
      return format(new Date(value), "PPpp")
    } catch (err) {
      return value
    }
  }
}

export const appendRefundSummary = (
  encoder: ThermalPrinterEncoder,
  width: number,
  details: RefundReceiptDetails
) => {
  const amount = formatNumberToCurrency(details.currency, details.refundAmount)
  const refundDate = formatRefundDateValue(details.refundedAt)

  encoder = printBorder(encoder, width, { noSpacing: true })
    .align("center")
    .bold(true)
    .line("REFUND SUMMARY")
    .bold(false)
    .newline()
    .align("left")
    .line(`Original Transaction: ${details.orderNumber || "-"}`)
    .line(`Refund Amount: ${amount}`)
    .line(`Payment Method: ${details.paymentMethod || "-"}`)
    .line(`Refunded By: ${details.refundedBy || "-"}`)
    .line(`Refund Date: ${refundDate}`)
    .newline()

  return encoder
}

export const createRefundSummaryBuffer = (
  details: RefundReceiptDetails,
  encoderOptions: ThermalPrinterEncoderOption = {
    language: "esc-pos",
    columns: 32,
    feedBeforeCut: 1,
  }
) => {
  const width = encoderOptions?.columns || 32
  let encoder = new ThermalPrinterEncoder({
    ...encoderOptions,
    columns: width,
    language: encoderOptions?.language || "esc-pos",
    imageMode: "raster",
  }).initializeWithMultiByte()

  encoder = appendRefundSummary(encoder, width, details)

  return encoder.encode()
}

export const printIminRefundSummary = (
  printer: IminPrinter,
  details: RefundReceiptDetails
) => {
  const amount = formatNumberToCurrency(details.currency, details.refundAmount)
  const refundDate = formatRefundDateValue(details.refundedAt)

  printer.printAndLineFeed()
  printer.setAlignment(1)
  printer.printText("REFUND SUMMARY")
  printer.printAndLineFeed()
  printer.setAlignment(0)
  printer.printText(`Original Transaction: ${details.orderNumber || "-"}`)
  printer.printText(`Refund Amount: ${amount}`)
  printer.printText(`Payment Method: ${details.paymentMethod || "-"}`)
  printer.printText(`Refunded By: ${details.refundedBy || "-"}`)
  printer.printText(`Refund Date: ${refundDate}`)
  printer.printAndLineFeed()
}

// imin printer helpers
export const printIminCartTransactionReceiptMetadata = async (
  printer: IminPrinter,
  type: "Header" | "Footer",
  business: Business,
  trx: GetCartTransactionResponse,
  receiptSetting: InvoiceSettingsResponseBody[number],
  posSetting: typeof DEFAULT_MPOS_SETTING
) => {
  const dueDate = () => {
    if (!trx.dueDate) return

    printer.setAlignment(1)
    printer.printText(
      `Due Date: ${format(parseISO(trx.dueDate as string), "dd/MM/yyyy")}`
    )
  }

  const vat = () => {
    printer.setAlignment(1)
    printer.printText(trx.location.companyName || "")
    printer.printText(`TIN: ${trx.location.tin}`)
    printer.printText(`${trx.location.addressLine1}`)
    printer.printText(`SN No.: ${trx.cashRegister?.serialNo}`)
    printer.printText(`MIN: ${trx.cashRegister?.min}`)
    printer.printAndLineFeed()

    if (business.id !== "9e800468-ce7e-4f26-ae19-61656ee51953") {
      printer.printText("THIS SERVES AS YOUR OFFICIAL RECEIPT")
    }
  }

  const transactionType = () => {
    printer.setAlignment(1)
    printer.printText(
      `Transaction Type: ${formatPaymentMethod(
        trx.paymentMethodCode,
        trx.paymentChannel
      )}`
    )
  }

  const orderType = () => {
    printer.setAlignment(1)
    printer.printText(`Order Type: ${trx.orderType}`)
  }

  const phone = () => {
    printer.setAlignment(1)
    printer.printText(business.phone)
  }

  const customer = () => {
    if (trx.isWalkIn) {
      printer.setAlignment(1)
      printer.printText("Customer: Walk In")
      printer.printAndLineFeed()
    } else {
      let address = trx.customerLocation?.addressLine1
      if (trx.customerLocation?.addressLine2) {
        address += `, ${trx.customerLocation?.addressLine2}`
      }
      if (trx.customerLocation?.city) {
        address += `, ${trx.customerLocation?.city}`
      }
      if (trx.customerLocation?.province) {
        address += `, ${trx.customerLocation?.province}`
      }
      if (trx.customerLocation?.zip) {
        address += `, ${trx.customerLocation?.zip}`
      }

      printer.setAlignment(1)
      printer.printText(`Customer: ${trx.customer?.fullName}`)
      printer.printText(`Phone: ${trx.customer?.phone}`)
      printer.printText(`Address: ${address}`)
      if (trx?.rewardedPoints != null) {
        printer.printText(`Rewarded Points: ${trx.rewardedPoints}`)
      }
      if (trx?.customer?.points != null) {
        printer.printText(`Total Points: ${trx.customer?.points}`)
      }
      if (trx?.rewardedCashback != null) {
        printer.printText(`Rewarded Cashback: ${trx.rewardedCashback}`)
      }
      if (trx?.customer?.cashback != null) {
        printer.printText(`Total Cashback: ${trx.customer?.cashback}`)
      }
      printer.printAndLineFeed()
    }
  }

  const note = () => {
    printer.setAlignment(1)
    printer.printText(`Note: ${trx.notes}`)
    printer.printAndLineFeed()
  }

  const barcode = () => {
    printer.setAlignment(1)
    printer.setBarCodeWidth(4)
    printer.setBarCodeHeight(64)
    printer.printBarCode(5, trx.orderNumber.toString())
    printer.printAndLineFeed()
    printer.printText(trx.orderNumber.toString())
    printer.printAndLineFeed()
  }

  const storeName = () => {
    printer.setAlignment(1)
    printer.printText(trx.location.name)
  }

  const location = () => {
    printer.setAlignment(1)
    printer.printText(trx.location.addressLine1)
    printer.printAndLineFeed()
  }

  const message = () => {
    printer.setAlignment(1)
    printer.printText(receiptSetting.message)
    printer.printAndLineFeed()
  }

  if (type === "Header") {
    printer.setAlignment(1)
    const logo = receiptSetting?.printLogo || business?.logoUrl || ""
    if (logo) {
      const loadImage = (src: string) => {
        return new Promise<HTMLImageElement>((resolve, reject) => {
          axios
            .get(src + `?v=${nanoid()}`, {
              responseType: "blob",
              headers: { "Cache-Control": "no-cache" },
            })
            .then((response) => {
              const img = new Image()
              img.crossOrigin = "anonymous"
              img.src = URL.createObjectURL(response.data)

              img.onload = () => {
                URL.revokeObjectURL(img.src) // Clean up
                resolve(img)
              }
              img.onerror = (err) => {
                URL.revokeObjectURL(img.src)
                reject(err)
              }
            })
        })
      }
      const image = await loadImage(logo)

      // Set the maximum width and height
      const maxWidth = 40 // Desired maximum width
      const maxHeight = 40 // Desired maximum height

      // Calculate the scaling factor to maintain aspect ratio
      const ratio = Math.min(maxWidth / image.width, maxHeight / image.height)
      const newWidth = image.width * ratio
      const newHeight = image.height * ratio

      const canvas = document.createElement("canvas")
      canvas.width = newWidth
      canvas.height = newHeight
      const ctx = canvas.getContext("2d")

      // Draw the image with the new dimensions
      ctx?.drawImage(image, 0, 0, newWidth, newHeight)

      const base64Image = canvas.toDataURL("image/png")
      await printer.printSingleBitmap(base64Image)
    }

    printer.printText("test2" + business.name)

    printer.printAndLineFeed()
  } else {
    printer.printAndLineFeed()
  }

  if (
    receiptSetting.showDueDate === type &&
    trx.paymentMethodCode === "PAY LATER"
  ) {
    dueDate()
  }
  if (trx.location.isVatRegistered === "Yes") {
    vat()
  }
  if (receiptSetting.showTaxPIN && receiptSetting.taxPIN) {
    printer.setAlignment(1)
    printer.printText(`Tax PIN: ${receiptSetting.taxPIN}`)
    printer.printAndLineFeed()
  }
  if (receiptSetting.transactionType === type) {
    transactionType()
  }
  if (receiptSetting.showOrderType === type) {
    orderType()
  }
  if (type === "Header") {
    printer.printText(new Date(trx.createdAt).toLocaleString())
  }
  if (receiptSetting.showPhone === type) {
    phone()
  }
  if (type === "Header") {
    printer.printText("Cashier: " + trx.createdBy)
    printer.printAndLineFeed()
  }

  // Handle delivery details for IMIN printer
  if (trx.shipment?.shipmentType === "POS_MANUAL" && type === "Header") {
    const { quoteDetails } = trx.shipment
    const {
      bookingCode,
      customerName,
      address,
      city,
      phoneNumber,
      courierName,
    } = quoteDetails

    printer.setAlignment(0)
    printer.printText(`Order Number: ${trx.orderNumber}`)
    printer.printText(`Booking Code: ${bookingCode || "-"}`)
    printer.printText(`Customer: ${customerName || "-"}`)
    printer.printText(`Address: ${address || "-"}`)
    printer.printText(`City: ${city || "-"}`)
    printer.printText(`Phone Number: ${phoneNumber || "-"}`)

    if (courierName) {
      printer.printText(`Courier Name: ${courierName}`)
    }

    printer.printAndLineFeed()
  }

  if (receiptSetting.showCustomerInfo === type) {
    customer()
  }
  if (receiptSetting.showOrderNote === type && trx.notes) {
    note()
  }
  if (receiptSetting.showBarcode === type) {
    barcode()
  } else {
    printer.setTextLineSpacing(`Order #${trx.orderNumber}`)
  }
  if (receiptSetting.storeName === type) {
    storeName()
  }
  if (receiptSetting.showLocation === type) {
    location()
  }
  if (receiptSetting.showMessage === type && receiptSetting.message) {
    message()
  }
}

export const printIminCartTransactionReceiptItemHeader = (
  printer: IminPrinter,
  trx: GetCartTransactionResponse
) => {
  printer.printAndLineFeed()
  printer.printAndLineFeed()

  printer.printColumnsText(
    ["Item(s)", trx.orderedProducts.length.toString()],
    [1, 2],
    [0, 2],
    [26, 26],
    576
  )
}

export const printIminCartTransactionReceiptTotal = (
  printer: IminPrinter,
  trx: GetCartTransactionResponse,
  currency?: string,
  business?: Business
) => {
  // Special logic for business ID "865ca7d1-e12a-401e-8c71-92f7a3ec2aa4"
  const shouldDeductIncludedTax =
    business?.id === "865ca7d1-e12a-401e-8c71-92f7a3ec2aa4"
  const includedTax = shouldDeductIncludedTax
    ? trx.taxes?.reduce((sum, { type, amount }) => {
        if (type === "INCLUDED") {
          return sum + amount
        }
        return sum
      }, 0) || 0
    : 0

  const adjustedTotal = trx.totalCartAmount - includedTax

  printer.printColumnsText(
    ["Subtotal", currency + " " + adjustedTotal.toLocaleString()],
    [1, 2],
    [0, 2],
    [26, 26],
    576
  )

  if (trx.discount > 0) {
    printer.printColumnsText(
      [
        "Discount",
        currency +
          " " +
          (trx.nonTaxDiscounts || trx.discount)?.toLocaleString(),
      ],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
  }
  if (trx.tax > 0) {
    printer.printColumnsText(
      ["Tax", currency + " " + trx.tax.toLocaleString()],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
  }
  if (trx.serviceCharge) {
    printer.printColumnsText(
      ["Service Charge", currency + " " + trx.serviceCharge.toLocaleString()],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
  }
  if (trx.shippingCost) {
    printer.printColumnsText(
      ["Shipping Cost", currency + " " + trx.shippingCost.toLocaleString()],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
  }
  if (trx.cashbackApplied) {
    printer.printColumnsText(
      [
        "Cashback Applied",
        currency + " " + trx.cashbackApplied.toLocaleString(),
      ],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
  } else if (trx.discount) {
    printer.printColumnsText(
      ["Discount", currency + " " + trx.discount.toLocaleString()],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
  }
  printer.printColumnsText(
    ["Total", currency + " " + trx.grandTotal.toLocaleString()],
    [1, 2],
    [0, 2],
    [26, 26],
    576
  )
  if (
    ["PAY LATER", "INVOICE"].includes(trx.paymentMethodCode) ||
    (trx.orderStatus !== "PAID" && trx.outstanding && trx.outstanding > 0)
  ) {
    printer.printColumnsText(
      [
        "Total Deposit",
        currency + " " + (trx.grandTotal - (trx.outstanding || 0)).toString(),
      ],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
    printer.printColumnsText(
      ["Total Outstanding", currency + " " + (trx.outstanding || 0).toString()],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
  }

  if (trx.paymentMethodCode === "CASH") {
    printer.printColumnsText(
      [
        "Amount Paid",
        currency + " " + getActualCashReceived(trx).toLocaleString(),
      ],
      [2, 2],
      [0, 2],
      [26, 26],
      576
    )
    printer.printColumnsText(
      ["Change", currency + " " + getActualCashChange(trx).toLocaleString()],
      [2, 2],
      [0, 2],
      [26, 26],
      576
    )
  }

  if (trx.paymentMethodCode === "SPLIT_PAYMENT") {
    const splitPaymentDetails = trx.splitPaymentDetails
    if (splitPaymentDetails && splitPaymentDetails.length) {
      printer.printText("Split Payments")
      splitPaymentDetails.forEach(
        ({ paymentMethodCode, paymentChannel, amount }) => {
          let paymentMethod = `${paymentMethodCode}`
          if (paymentMethod === "CUSTOM" && paymentChannel) {
            paymentMethod = paymentChannel.toUpperCase()
          }
          printer.printColumnsText(
            [paymentMethod, currency + " " + amount.toLocaleString()],
            [2, 2],
            [0, 2],
            [26, 26],
            576
          )
        }
      )
    }
  }

  if (trx.pickupTime) {
    printer.printColumnsText(
      ["Pickup Date", format(parseISO(trx.pickupTime), "dd MMM yyyy")],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
  }

  if (trx.deliverectOrderType) {
    printer.printColumnsText(
      ["Order Type", DELIVERECT_ORDER_TYPE_LABELS[trx.deliverectOrderType]],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
    printer.printColumnsText(
      [
        "Delivery Channel",
        `${trx.source} (${trx.deliverectChannelOrderDisplayId})`,
      ],
      [1, 2],
      [0, 2],
      [26, 26],
      576
    )
    if (trx.deliverectPickupTime) {
      printer.printColumnsText(
        [
          "Pickup Time",
          format(parseISO(trx.deliverectPickupTime), "dd MMM yyyy HH:mm"),
        ],
        [1, 2],
        [0, 2],
        [26, 26],
        576
      )
    }
    if (trx.deliverectDeliveryTime) {
      printer.printColumnsText(
        [
          "Delivery Time",
          format(parseISO(trx.deliverectDeliveryTime), "dd MMM yyyy HH:mm"),
        ],
        [1, 2],
        [0, 2],
        [26, 26],
        576
      )
    }
  }
}

export const printIminCartTransactionReceiptItem = (
  printer: IminPrinter,
  product: GetCartTransactionResponse["orderedProducts"][number],
  currency?: string
) => {
  const variantName = product.productOptionValues
    ?.map((el) => el.name)
    .join(" / ")

  let item =
    product.name +
    ` - ${product.quantity}x@${product.sellingPrice.toLocaleString()}`

  if (variantName) {
    item += `(${variantName})`
  }

  printer.setAlignment(0)
  printer.printText(item)
  // printer.printColumnsText(
  //   [item, product.totalPrice.toLocaleString()],
  //   [1, 1],
  //   [0, 2],
  //   [26, 26],
  //   576
  // )

  if (product.notes) {
    printer.printText(`Notes: ${product.notes}`)
  }

  if (!!product.orderedProductAddOns?.length) {
    printer.printText("Add Ons:")
    product.orderedProductAddOns.forEach((el) => {
      printer.printText(
        `${el.name} - ${el.quantity}x@${el.sellingPrice.toLocaleString()}`
      )
    })
  }

  if (!!product.employees?.length) {
    printer.printText("Employees:")
    product.employees.forEach((el) => {
      printer.printText(el.fullName)
    })
  }

  printer.setAlignment(2)
  printer.printText(currency + " " + product.totalPrice.toLocaleString())
  printer.printAndLineFeed()
  printer.setAlignment(0)
}

export const printFullReceipt = async (
  business: Business,
  trx: GetCartTransactionResponse,
  receiptSetting: InvoiceSettingsResponseBody[number],
  posSetting: typeof DEFAULT_MPOS_SETTING,
  currency: string,
  encoderOptions: ThermalPrinterEncoderOption = {
    language: "esc-pos",
    columns: 32,
    feedBeforeCut: 1,
  },
  hasTaxes?: boolean,
  refundDetails?: RefundReceiptDetails
) => {
  const width = encoderOptions?.columns || 32
  let encoder = new ThermalPrinterEncoder({
    ...encoderOptions,
    imageMode: "raster",
  }).initializeWithMultiByte()
  encoder = await encodeCartTransactionReceiptMetadata(
    "Header",
    business,
    trx,
    receiptSetting,
    posSetting,
    width,
    encoder,
    hasTaxes
  )
  encoder = encodeCartTransactionReceiptItemHeader(trx, width, encoder)
  for (const item of trx.orderedProducts) {
    const isRefunded = trx?.refund?.refundItem?.some(
      (r) => r.orderedProduct?.id === item.id
    )
    const isExchanged =
      !!trx?.refund?.acceptedDate &&
      new Date(item.created_at) > new Date(trx?.refund?.acceptedDate)
    encoder = encodeCartTransactionReceiptItem(
      item,
      width,
      currency,
      encoder,
      isRefunded,
      isExchanged
    )
  }
  encoder = printBorder(encoder, width, { noSpacing: true })
  encoder = encodeCartTransactionReceiptTotal(
    trx,
    width,
    currency,
    receiptSetting,
    encoder,
    business
  )
  if (refundDetails) {
    encoder = appendRefundSummary(encoder, width, refundDetails)
  }
  encoder = await encodeCartTransactionReceiptMetadata(
    "Footer",
    business,
    trx,
    receiptSetting,
    posSetting,
    width,
    encoder,
    hasTaxes
  )
  // return encoder.cut().encode()
  return encoder.newline(3).cut().encode()
}
